import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import MySnackbar from "./MySnackbar";
import WebhookDialog from "./WebhookDialog/WebhookDialog";
import useSnackbar from "../hooks/useSnackbar";

import {
  getWebhooks,
  deleteWebhook,
  testWebhook,
} from "../http/webhookAPI";

const WebhooksSettings = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [testingWebhookId, setTestingWebhookId] = useState(null);
  const snack = useSnackbar(false, "Ошибка");

  const fetchWebhooks = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getWebhooks();
      setWebhooks(data);
    } catch (error) {
      snack.handleChange(true, "Ошибка при загрузке веб-хуков", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWebhooks();
  }, [fetchWebhooks]);

  const handleAddClick = () => {
    setSelectedWebhook(null);
    setOpenDialog(true);
  };

  const handleEditClick = (webhook) => {
    setSelectedWebhook(webhook);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedWebhook(null);
  };

  const handleDialogSave = () => {
    fetchWebhooks();
    handleDialogClose();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Вы уверены, что хотите удалить этот веб-хук?")) {
      return;
    }

    try {
      await deleteWebhook(id);
      setWebhooks((current) => current.filter((webhook) => webhook.id !== id));
      snack.handleChange(true, "Веб-хук успешно удален", "success");
    } catch (error) {
      snack.handleChange(
        true,
        error.response?.data?.message || "Ошибка при удалении веб-хука",
        "error"
      );
    }
  };

  const handleTest = async (id) => {
    try {
      setTestingWebhookId(id);
      const result = await testWebhook(id);

      if (result.success) {
        snack.handleChange(
          true,
          `Тестовый запрос успешно выполнен. Статус: ${result.log.responseStatus}`,
          "success"
        );
      } else {
        snack.handleChange(
          true,
          `Ошибка при тестировании: ${result.error}`,
          "error"
        );
      }
    } catch (error) {
      snack.handleChange(
        true,
        error.response?.data?.message || "Ошибка при тестировании веб-хука",
        "error"
      );
    } finally {
      setTestingWebhookId(null);
    }
  };

  const getEventDescription = (type) => {
    const descriptions = {
      changeUserSessionStatus: "Изменение статуса сессии",
      userMatched: "Сопоставление пользователя",
      changeMarketingMailingToggle: "Вкл/выкл маркетинговых рассылок"
    };
    return descriptions[type] || type;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200 }}>
      <MySnackbar {...snack} />

      <Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6">Веб-хуки</Typography>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleAddClick}
        >
          Добавить веб-хук
        </Button>
      </Box>

      <Typography sx={{ mb: 2 }} color="text.secondary">
        Веб-хуки позволяют отправлять HTTP-запросы на ваш сервер при
        определенных событиях в системе.
      </Typography>

      {webhooks.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography color="text.secondary">
            Веб-хуки не настроены. Нажмите "Добавить веб-хук" для создания
            первого веб-хука.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Событие</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Флаги</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {webhooks.map((webhook) => (
                <TableRow key={webhook.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {getEventDescription(webhook.type)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {webhook.type}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 300,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {webhook.url}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      {webhook.requiredFlag && (
                        <Typography variant="caption" color="text.secondary">
                          Требуется: {webhook.requiredFlag}
                        </Typography>
                      )}
                      {webhook.successFlag && (
                        <Typography variant="caption" color="text.secondary">
                          Успех: {webhook.successFlag}
                        </Typography>
                      )}
                      {!webhook.requiredFlag && !webhook.successFlag && (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={webhook.isActive ? "Активен" : "Неактивен"}
                      color={webhook.isActive ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleTest(webhook.id)}
                      disabled={testingWebhookId === webhook.id}
                      title="Тестировать"
                    >
                      {testingWebhookId === webhook.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <PlayArrowIcon />
                      )}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick(webhook)}
                      title="Редактировать"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(webhook.id)}
                      title="Удалить"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {openDialog && (
        <WebhookDialog
          open={openDialog}
          webhook={selectedWebhook}
          onClose={handleDialogClose}
          onSave={handleDialogSave}
        />
      )}
    </Box>
  );
};

export default WebhooksSettings;
